import { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import apiBase from '../apiConfig';
import { getAuthHeaders } from '../helpers/utils';
import AppContext from '../context/Context';
import { filterRoutesByPermissions, hasPermission } from '../helpers/permissions';
import {
  RESULT_TOPICS_EVENT,
  buildNavigationRoutes,
  emitResultTopicsUpdate,
  getCachedResultTopicRoutes
} from '../helpers/resultTopics';

const TOPICS_ENDPOINT = `${apiBase}/p-admin/test-results.php?action=topics`;

let pendingTopicRequest = null;

const fetchTopicRoutes = async headers => {
  const response = await axios.get(TOPICS_ENDPOINT, {
    headers
  });

  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Natijalar menyusi yuklanmadi.');
  }

  return emitResultTopicsUpdate(response.data?.data?.topics || []);
};

const useAdminNavigationRoutes = () => {
  const { user } = useContext(AppContext);
  const authHeaders = useMemo(() => getAuthHeaders(), []);
  const [routes, setRoutes] = useState(() => buildNavigationRoutes(getCachedResultTopicRoutes()));

  useEffect(() => {
    let active = true;

    const applyRoutes = topicRoutes => {
      if (!active) {
        return;
      }

      setRoutes(buildNavigationRoutes(topicRoutes));
    };

    const syncTopics = async () => {
      try {
        if (!pendingTopicRequest) {
          pendingTopicRequest = fetchTopicRoutes(authHeaders).finally(() => {
            pendingTopicRequest = null;
          });
        }

        const topicRoutes = await pendingTopicRequest;
        applyRoutes(topicRoutes);
      } catch (error) {
        applyRoutes(getCachedResultTopicRoutes());
      }
    };

    if (!hasPermission(user, 'padmin.analytics.view')) {
      applyRoutes([]);
      return () => {
        active = false;
      };
    }

    const cachedRoutes = getCachedResultTopicRoutes();
    if (cachedRoutes.length > 0) {
      applyRoutes(cachedRoutes);
    } else {
      syncTopics();
    }

    const handleTopicsChange = event => {
      applyRoutes(Array.isArray(event.detail) ? event.detail : getCachedResultTopicRoutes());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(RESULT_TOPICS_EVENT, handleTopicsChange);
    }

    return () => {
      active = false;

      if (typeof window !== 'undefined') {
        window.removeEventListener(RESULT_TOPICS_EVENT, handleTopicsChange);
      }
    };
  }, [authHeaders, user]);

  return useMemo(() => filterRoutesByPermissions(routes, user), [routes, user]);
};

export default useAdminNavigationRoutes;
